/**
 * Contract every file renderer in the pin viewer meets.
 *
 * A renderer turns one file into a list of page elements in the DOM — a PDF or Word page, a slide,
 * a worksheet, an image. The viewer lays its pin overlay on top of each one, so pins work the same
 * whatever the format: they are stored relative to the page element, not the file.
 */
export interface RenderedPage {
    el: HTMLElement;
    /** `Page 2`, `Slide 3`, `Sheet: Rates`, `Image`. */
    label: string;
}

export interface PageRenderer {
    /** One page shown at a time (worksheets), switched by the viewer's tabs. */
    readonly paged: boolean;
    /** Whether the content carries selectable text, so text pins are offered. */
    readonly hasText: boolean;

    /** Renders `src` into `host` and resolves its pages, in order. */
    render(src: string, host: HTMLElement): Promise<RenderedPage[]>;

    /** Resizes to `zoom` percent, where 100 fits the host width. Page elements are kept. */
    zoom(zoom: number): Promise<void>;

    destroy(): void;
}

/**
 * Fetches a file's bytes.
 *
 * ⚠️ The sample files are public assets. Once the backend serves the material, attach the
 * `Authorization` header from `SessionStorageService.getCurrentUserAuthToken()` here, following
 * the manual per-request pattern used across the app.
 */
export async function fetchFile(src: string): Promise<ArrayBuffer> {
    const response: Response = await fetch(src);
    if (!response.ok) {
        throw new Error(`Could not load ${src} (${response.status}).`);
    }
    return response.arrayBuffer();
}

/** Room the host keeps either side of a fitted page. */
export const FIT_GUTTER_PX: number = 24;

/** Factor that fits `naturalWidth` into the host, never enlarging past natural size. */
export function fitFactor(host: HTMLElement, naturalWidth: number): number {
    if (!naturalWidth) {
        return 1;
    }
    return Math.min(1, Math.max(200, host.clientWidth - FIT_GUTTER_PX) / naturalWidth);
}
